import { patchSaveApplication } from "../../services/network";
import { getCookie } from "../../utils/Common";

// Shared by the per-test mitigation/compensating factor dropdown (processor
// request + underwriter action) and the loan-level General Waiver request —
// both use the exact same list, so General Waiver's export is just an alias.
export const MITIGATION_FACTOR_OPTIONS = [
  "Credit Score above minimum by 10 points or greater",
  "Repeat borrower with 2 or more prior loans performing/performed",
  "Experienced borrower with at least 4 rental properties",
  "Reduction in property debt service by 10% or greater",
  "DSCR above min by 0.10x or greater",
  "0X30X24 month mortgage history",
  "LTV/LTC below max by 5% or greater",
  "PITIA reserves above minimum",
  "Other",
];

export const GENERAL_WAIVER_MITIGATION_FACTOR_OPTIONS = MITIGATION_FACTOR_OPTIONS;

export const WaiverStatus = {
  PENDING: "Pending Review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  APPROVED_CONDITIONALLY: "Approved Conditionally",
} as const;

export const MIN_MITIGATION_FACTORS = 2;

// Maps each decided status to the pair of audit field names on WaiverAction
// that should be stamped when that specific outcome is chosen.
export const ACTION_AUDIT_FIELDS: Record<
  string,
  { by: "approvedBy" | "rejectedBy" | "approvedConditionallyBy"; on: "approvedOn" | "rejectedOn" | "approvedConditionallyOn" }
> = {
  [WaiverStatus.APPROVED]: { by: "approvedBy", on: "approvedOn" },
  [WaiverStatus.REJECTED]: { by: "rejectedBy", on: "rejectedOn" },
  [WaiverStatus.APPROVED_CONDITIONALLY]: {
    by: "approvedConditionallyBy",
    on: "approvedConditionallyOn",
  },
};

// Which by/on pair is relevant for the waiver's *current* status — used to
// show "who approved/rejected/conditionally-approved this, and when" without
// the caller needing to know the field-name mapping.
export const getCurrentActionAudit = (waiver: {
  status: string;
  underwriterAction?: any;
}): { by?: string; on?: string } | null => {
  const fields = ACTION_AUDIT_FIELDS[waiver.status];
  if (!fields || !waiver.underwriterAction) return null;
  return {
    by: waiver.underwriterAction[fields.by],
    on: waiver.underwriterAction[fields.on],
  };
};

export const getFullName = (decodedTokenData: any): string =>
  decodedTokenData?.name ||
  (decodedTokenData?.given_name && decodedTokenData?.family_name
    ? `${decodedTokenData.given_name} ${decodedTokenData.family_name}`
    : "") ||
  decodedTokenData?.email ||
  decodedTokenData?.user_id ||
  "Unknown User";

// Older waivers were created back when Mitigation Factor was a single-select,
// so `mitigationFactor` on stored records can still be a plain string rather
// than an array. Normalize on read so the multi-select UI never chokes on it.
export const asMitigationFactorArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value) return [value];
  return [];
};

let waiverIdFallbackCounter = 0;

// Generates an id for loan-level General Waivers, which (unlike per-test
// waivers) have no takeoutPartner/testTitle to key off of. This id is only
// ever used as a local key to match a waiver record — never as a security
// token — but still avoids Math.random() (an insecure PRNG per SonarQube
// javascript:S2245) in favor of the Web Crypto API, falling back further to
// a counter (not a PRNG) on the rare runtime with no crypto object at all.
export const generateWaiverId = (): string => {
  const cryptoObj: any = typeof crypto !== "undefined" ? crypto : undefined;
  if (typeof cryptoObj?.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  if (typeof cryptoObj?.getRandomValues === "function") {
    const bytes: Uint8Array = cryptoObj.getRandomValues(new Uint8Array(16));
    return `${Date.now()}-${Array.from(bytes, (b: number) => b.toString(16).padStart(2, "0")).join("")}`;
  }
  waiverIdFallbackCounter += 1;
  return `${Date.now()}-${waiverIdFallbackCounter}`;
};

const normalizeWaiverRecord = (updated: any) => ({
  ...updated,
  mitigationFactor: asMitigationFactorArray(updated?.mitigationFactor),
  ...(updated?.underwriterAction
    ? {
        underwriterAction: {
          ...updated.underwriterAction,
          compensatingFactor: asMitigationFactorArray(
            updated.underwriterAction.compensatingFactor,
          ),
        },
      }
    : {}),
});

// A waiver is a loan-level "General Waiver" (TA-11746) if it carries an
// `id` rather than a takeoutPartner/testTitle pair — those live in a
// separate `generalWaiverDetails` array so they're never mixed with
// per-test waivers in `waiverDetails`.
const getWaiverListField = (waiver: { id?: string }) =>
  waiver?.id ? "generalWaiverDetails" : "waiverDetails";

const waiverMatcher =
  (target: { id?: string; takeoutPartner?: string; testTitle?: string }) =>
  (item: any) =>
    target.id
      ? item.id === target.id
      : item.takeoutPartner === target.takeoutPartner &&
        item.testTitle === target.testTitle;

// Shared save path for the request forms and the underwriter action form:
// finds the matching waiver in the loan's waiver list, replaces it with
// `updated`, patches the loan, and syncs LoansContext. Works for both
// per-test waivers (matched by takeoutPartner + testTitle, stored in
// `waiverDetails`) and General Waivers (matched by `id`, stored in
// `generalWaiverDetails`) — which list/matcher to use is inferred from
// whether `target.id` is set. `mitigationFactor`/`compensatingFactor` are
// always normalized to an array before they're written, so they're never
// persisted (or displayed back) as a single stray string.
export const persistWaiverUpdate = async (
  loanId: string | undefined,
  loanDetails: any,
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  target: { id?: string; takeoutPartner?: string; testTitle?: string },
  updated: any,
): Promise<void> => {
  const fieldName = getWaiverListField(target);
  const matches = waiverMatcher(target);
  const normalizedUpdate = normalizeWaiverRecord(updated);

  const updatedList = (loanDetails?.[fieldName] ?? []).map((item: any) =>
    matches(item) ? normalizedUpdate : item,
  );

  await patchSaveApplication(loanId, { [fieldName]: updatedList });
  setLoanDetails?.((prev: any) => ({
    ...prev,
    [fieldName]: updatedList,
  }));
};

// Appends a brand-new waiver to the appropriate list (inferred the same way
// as persistWaiverUpdate) rather than replacing an existing entry — used by
// both request forms on initial submit.
export const persistNewWaiver = async (
  loanId: string | undefined,
  loanDetails: any,
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  newWaiver: any,
): Promise<void> => {
  const fieldName = getWaiverListField(newWaiver);
  const normalizedNewWaiver = normalizeWaiverRecord(newWaiver);
  const updatedList = [...(loanDetails?.[fieldName] ?? []), normalizedNewWaiver];

  await patchSaveApplication(loanId, { [fieldName]: updatedList });
  setLoanDetails?.((prev: any) => ({
    ...prev,
    [fieldName]: updatedList,
  }));
};

// A file selected in a waiver request form is uploaded to GCS immediately
// (before Submit), so removing it from the pending-upload list alone leaves
// the object orphaned in the bucket. Same soft-delete-and-patch approach as
// DocumentList.tsx's handleDeleteDocument: append a deleted-marker record to
// the loan's shared `documents` list (never shown, since every consumer of
// that list filters out isDeleted entries) so the backend can clean up the
// underlying GCS object for it.
export const softDeleteUploadedDocument = async (
  loanId: string | undefined,
  loanDetails: any,
  setLoanDetails: ((updater: (prev: any) => any) => void) | undefined,
  doc: { fileName: string; filePath: string },
): Promise<void> => {
  const deletedEntry = {
    documentName: doc.fileName,
    documentPath: doc.filePath,
    isDeleted: true,
    deletedOn: new Date().toISOString(),
    deletedBy: getCookie("partyId") ?? "unknown",
  };
  const updatedDocuments = [...(loanDetails?.documents ?? []), deletedEntry];

  await patchSaveApplication(loanId, { documents: updatedDocuments });
  setLoanDetails?.((prev: any) => ({
    ...prev,
    documents: updatedDocuments,
  }));
};
